import Axios from 'axios';
import { toast } from 'react-toastify';
const API_URL = process.env.REACT_APP_API_URL;

// Function to create virtual meeting
export const handleCreateMeetingAndSendInvitation = async ({
    event,
    formData,
    selectedPatient,
    userEmail,
    setShowModal,
    setFormData,
    setLoading,
    setError,
    tokenClient,
    isCreating,
    setIsCreating,
}) => {
    event.preventDefault();
    event.persist(); // Ensure event is reusable in async context

    // Validate form fields
    if (
        String(formData.appointmentDate).trim() === '' ||
        String(formData.timeSlotStart).trim() === '' ||
        String(formData.timeSlotEnd).trim() === '' ||
        String(formData.patientId).trim() === ''
    ) {
        toast.error('Please enter all required fields', {
            position: 'top-center',
        });
        return;
    }

    if (!tokenClient) {
        setError('Google API or GIS not loaded');
        console.error('Cannot create meeting: GIS or gapi not loaded');
        toast.error('Google API or GIS not loaded', {
            position: 'top-center',
        });
        return;
    }

    if (isCreating) {
        console.log('Meeting creation in progress, ignoring request');
        return;
    }

    setIsCreating(true);
    setError(null);

    try {
        setLoading(true);

        // Step 1: Request access token
        const tokenResponse = await new Promise((resolve, reject) => {
            tokenClient.callback = (response) => {
                if (response.error) {
                    console.error('Token error:', response);
                    setError(`Token error: ${response.error}`);
                    reject(new Error(response.error));
                    return;
                }
                window.gapi.client.setToken({ access_token: response.access_token });
                console.log('Access token set:', response.access_token);
                resolve(response);
            };
            tokenClient.requestAccessToken();
        });

        if (!window.gapi.client.getToken()) {
            setError('No access token available');
            console.error('No access token available after request');
            toast.error('No access token available', {
                position: 'top-center',
            });
            return;
        }

        // Step 2: Validate and prepare event dates
        const startDateTime = new Date(`${formData.appointmentDate}T${formData.timeSlotStart}`);
        const endDateTime = new Date(`${formData.appointmentDate}T${formData.timeSlotEnd}`);

        if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
            setError('Invalid date or time format');
            console.error('Invalid date or time:', formData.appointmentDate, formData.timeSlotStart, formData.timeSlotEnd);
            toast.error('Invalid date or time format', {
                position: 'top-center',
            });
            return;
        }

        if (endDateTime <= startDateTime) {
            setError('End time must be after start time');
            console.error('End time is not after start time:', startDateTime, endDateTime);
            toast.error('End time must be after start time', {
                position: 'top-center',
            });
            return;
        }

        // Step 3: Check for existing events to prevent duplicates
        const eventSummary = `Appointment with ${selectedPatient.patient_name || 'Patient'}`;
        const timeMin = new Date(startDateTime.getTime() - 30 * 60 * 1000).toISOString(); // 30 minutes before
        const timeMax = new Date(endDateTime.getTime() + 30 * 60 * 1000).toISOString(); // 30 minutes after
        const existingEvents = await window.gapi.client.calendar.events.list({
            calendarId: 'primary',
            timeMin,
            timeMax,
            q: eventSummary,
            singleEvents: true,
        });

        if (existingEvents.result.items.length > 0) {
            console.warn('Duplicate event detected:', existingEvents.result.items);
            toast.warn('An appointment already exists for this time and patient.', {
                position: 'top-center',
            });
            setShowModal(false);
            setFormData({
                appointmentDate: '',
                timeSlotStart: '',
                timeSlotEnd: '',
                meetLink: '',
                patientId: '',
            });
            return;
        }

        // Step 4: Create Google Calendar event
        const uniqueRequestId = `meet-${formData.patientId}-${Date.now()}`;
        const event = {
            summary: eventSummary,
            description: 'Automated appointment created through GetQuickCare',
            start: {
                dateTime: startDateTime.toISOString(),
                timeZone: 'America/New_York',
            },
            end: {
                dateTime: endDateTime.toISOString(),
                timeZone: 'America/New_York',
            },
            attendees: [
                { email: userEmail },
                { email: selectedPatient.patient_email },
            ],
            conferenceData: {
                createRequest: {
                    requestId: uniqueRequestId,
                    conferenceSolutionKey: { type: 'hangoutsMeet' },
                },
            },
            organizer: { email: userEmail },
        };

        const calendarResponse = await window.gapi.client.calendar.events.insert({
            calendarId: 'primary',
            resource: event,
            conferenceDataVersion: 1,
        });

        const meetLink = calendarResponse.result.hangoutLink;
        console.log('Event created successfully:', calendarResponse.result);
        console.log('Event ID:', calendarResponse.result.id);
        console.log('Meet link:', meetLink);

        // Step 5: Send invitation via backend API
        const invitationData = {
            ...formData,
            meetLink,
        };

        const invitationResponse = await Axios.post(
            `${API_URL}/appointment/send-invitation`,
            invitationData,
            {
                headers: {
                    Authorization: localStorage.getItem('token'),
                },
            }
        );

        if (invitationResponse.status === 201 && invitationResponse.data.success) {
            toast.success('Invitation sent successfully!', {
                position: 'top-center',
            });
            setShowModal(false);
            setFormData({
                appointmentDate: '',
                timeSlotStart: '',
                timeSlotEnd: '',
                meetLink: '',
                patientId: '',
            });
        } else {
            console.error('Failed to send invitation:', invitationResponse.data.message);
            toast.error(invitationResponse.data.message, {
                position: 'top-center',
            });
            // Attempt to delete the created event with retries
            let retryCount = 0;
            const maxRetries = 3;
            while (retryCount < maxRetries) {
                try {
                    await window.gapi.client.calendar.events.delete({
                        calendarId: 'primary',
                        eventId: calendarResponse.result.id,
                    });
                    console.log('Rolled back: Deleted calendar event:', calendarResponse.result.id);
                    break;
                } catch (deleteError) {
                    console.error(`Retry ${retryCount + 1} failed to delete calendar event:`, deleteError);
                    retryCount++;
                    if (retryCount === maxRetries) {
                        toast.error('Failed to clean up duplicate event. Please delete it manually.', {
                            position: 'top-center',
                        });
                    }
                    await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1s before retry
                }
            }
        }
    } catch (error) {
        console.error('Error in meeting creation or invitation:', error);
        setError(
            `Error: ${error.message || error.result?.error?.message || 'Unknown error'}`
        );
        toast.error(
            'Failed to create meeting or send invitation. Please try again.',
            {
                position: 'top-center',
            }
        );
    } finally {
        setIsCreating(false);
        setLoading(false);
    }
};

// Function to place orders for authenticated patients
export const handleCreateHomeCareMeeting = async ({ formData, setFormData, setShowModal, setLoading }) => {
    if (String(formData.appointmentDate).trim() === '' ||
        String(formData.timeSlotStart).trim() === '' ||
        String(formData.timeSlotEnd).trim() === '' ||
        String(formData.location).trim() === '' ||
        String(formData.patientId).trim() === '') {
        toast.error('Please enter all the required fields', {
            position: 'top-center',
        });
        return;
    }
    try {
        setLoading(true);
        const response = await Axios.post(`${API_URL}/appointment/send-home-care-invitation`, { ...formData }, {
            headers: {
                Authorization: localStorage.getItem("token"),
            }
        });

        if (response.status === 201 && response.data.success) {
            toast.success('Invitation sent successfully!', {
                position: 'top-center',
            });
            setShowModal(false); // Close the modal
            setFormData({ providerType: '', timeSlot: '', meetLink: '', patientId: '' }); // Reset the form
        } else {
            console.error('Failed to send invitation: ', response.data.message);
            toast.error(response.data.message, { position: 'top-center' });
        }
    } catch (error) {
        console.error('Failed to send invitation:', error);
        toast.error('An error occurred while sending invitation, please try again later!', {
            position: 'top-center',
        });
    } finally {
        setLoading(false);
    }
};

// Function to fetch booked appointments
export const fetchBookedAppointments = async (setAppointments) => {
    try {
        const response = await Axios.get(`${API_URL}/appointment/fetch-appointments`, {
            headers: {
                Authorization: localStorage.getItem("token"),
            },
        });

        if (response.status === 200 && response.data.success) {
            setAppointments(response.data.appointments);
        } else {
            console.error('Failed to fetch appointments:', response.data.message);
            toast.error('Failed to fetch appointments!', { position: 'top-center' });
        }
    } catch (error) {
        console.error('Error fetching appointments:', error);
        toast.error('An error occurred while fetching appointments. Please try again later!', {
            position: 'top-center',
        });
    }
};


// Function to confirm booking after Stripe success
export const handleConfirmBookingAfterPayment = async (sessionId, setProvider, navigate, setLoading) => {
    try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            toast.error('Please log in to confirm booking', { position: 'top-center' });
            return;
        }

        const response = await Axios.post(
            `${API_URL}/appointment/confirm-booking`,
            { sessionId },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            }
        );

        if (response.data.success) {
            setProvider && setProvider(response.data.assignedProvider);
            toast.success(`Booking confirmed with provider: ${response.data.assignedProvider.name}`, {
                position: 'top-center'
            });
            // Optionally, navigate after a delay
            // setTimeout(() => navigate('/dashboard'), 3000);
        } else {
            toast.error(response.data.message || 'Failed to confirm booking. Please try again.', {
                position: 'top-center'
            });
        }
    } catch (error) {
        console.error('Error confirming booking:', error);
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            navigate('/login');
            toast.error('Session expired. Please log in again.', { position: 'top-center' });
        } else {
            toast.error(error.response?.data?.message || 'Failed to confirm booking. Please try again.', {
                position: 'top-center'
            });
        }
    } finally {
        setLoading(false);
    }
};